import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, StickyNote, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed NoteItem.
import { type NoteItem, notesApi } from "../api";

type EditDraft = { title: string; body: string };

export function NotesPage() {
	const queryClient = useQueryClient();
	const [title, setTitle] = useState("");
	const [body, setBody] = useState("");
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editDraft, setEditDraft] = useState<EditDraft>({ title: "", body: "" });

	const { data: notes, isLoading } = useQuery({
		queryKey: ["notes"],
		queryFn: () => notesApi.list(),
	});

	const createNote = useMutation({
		mutationFn: (payload: { title: string; body: string | null }) =>
			notesApi.create({ input: payload }),
		onSuccess: () => {
			setTitle("");
			setBody("");
			queryClient.invalidateQueries({ queryKey: ["notes"] });
			toast.success("Note added");
		},
	});

	const updateNote = useMutation({
		mutationFn: ({ id, title, body }: { id: string; title: string; body: string | null }) =>
			notesApi.update({ params: { id }, input: { title, body } }),
		onSuccess: () => {
			setEditingId(null);
			queryClient.invalidateQueries({ queryKey: ["notes"] });
			toast.success("Note updated");
		},
	});

	const deleteNote = useMutation({
		mutationFn: (id: string) => notesApi.remove({ params: { id } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["notes"] });
			toast.success("Note deleted");
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = title.trim();
		if (trimmed.length === 0) return;
		createNote.mutate({ title: trimmed, body: body.trim() || null });
	};

	const startEdit = (note: NoteItem) => {
		setEditingId(note.id);
		setEditDraft({ title: note.title, body: note.body ?? "" });
	};

	const saveEdit = (id: string) => {
		const trimmedTitle = editDraft.title.trim();
		if (trimmedTitle.length === 0) {
			toast.error("Title can't be empty");
			return;
		}
		updateNote.mutate({
			id,
			title: trimmedTitle,
			body: editDraft.body.trim() || null,
		});
	};

	const count = notes?.length ?? 0;

	return (
		<>
			<PageHeader
				icon={StickyNote}
				title="Notes"
				badges={count > 0 ? <Badge tone="subtle">{count}</Badge> : undefined}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-2xl space-y-5">
					{/* Create form */}
					<form
						onSubmit={handleSubmit}
						className="rounded-lg border border-hairline bg-surface-1 p-4 space-y-3"
					>
						<Input
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder="New note title"
						/>
						<Textarea
							value={body}
							onChange={(e) => setBody(e.target.value)}
							placeholder="Body (optional)"
							rows={3}
						/>
						<div className="flex justify-end">
							<Button type="submit" disabled={createNote.isPending}>
								{createNote.isPending ? "Adding…" : "Add note"}
							</Button>
						</div>
					</form>

					{/* Loading */}
					{isLoading ? (
						<div className="rounded-lg border border-hairline bg-surface-1 overflow-hidden">
							{Array.from({ length: 3 }).map((_, i) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
									key={i}
									className="px-4 py-3 border-b border-hairline last:border-b-0 space-y-2"
								>
									<Skeleton w="55%" h={13} />
									<Skeleton w="30%" h={10} />
								</div>
							))}
						</div>
					) : notes && notes.length > 0 ? (
						<div className="rounded-lg border border-hairline bg-surface-1 overflow-hidden">
							{notes.map((note) => {
								const isEditing = editingId === note.id;
								return (
									<div key={note.id} className="border-b border-hairline last:border-b-0 px-4 py-3">
										{isEditing ? (
											<div className="space-y-2">
												<Input
													value={editDraft.title}
													onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
													placeholder="Title"
												/>
												<Textarea
													value={editDraft.body}
													onChange={(e) => setEditDraft((d) => ({ ...d, body: e.target.value }))}
													placeholder="Body (optional)"
													rows={3}
												/>
												<div className="flex justify-end gap-2">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => setEditingId(null)}
													>
														<X className="size-3.5 mr-1" />
														Cancel
													</Button>
													<Button
														type="button"
														size="sm"
														onClick={() => saveEdit(note.id)}
														disabled={updateNote.isPending}
													>
														{updateNote.isPending ? "Saving…" : "Save"}
													</Button>
												</div>
											</div>
										) : (
											<div className="flex items-start justify-between gap-3 group">
												<div className="flex-1 min-w-0">
													<div className="text-body-sm font-medium text-foreground">
														{note.title}
													</div>
													<div className="text-meta text-ink-subtle mt-0.5">
														{new Date(note.createdAt).toLocaleString()}
													</div>
													{note.body ? (
														<div className="text-body-sm text-ink-muted mt-1.5 whitespace-pre-wrap">
															{note.body}
														</div>
													) : null}
												</div>
												<div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
													<Button
														type="button"
														variant="ghost"
														size="icon-sm"
														onClick={() => startEdit(note)}
														aria-label="Edit note"
													>
														<Pencil className="size-3.5" />
													</Button>
													<ConfirmDialog
														title="Delete this note?"
														description={`"${note.title}" will be permanently removed.`}
														confirmLabel="Delete"
														destructive
														onConfirm={() => deleteNote.mutate(note.id)}
													>
														<Button
															type="button"
															variant="ghost"
															size="icon-sm"
															aria-label="Delete note"
														>
															<Trash2 className="size-3.5" />
														</Button>
													</ConfirmDialog>
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					) : (
						<EmptyState
							icon={StickyNote}
							title="No notes yet"
							body="Capture ideas, links, or anything worth remembering."
							primary={
								<Button
									type="button"
									onClick={() => {
										const input = document.querySelector<HTMLInputElement>(
											"form input[placeholder='New note title']",
										);
										input?.focus();
									}}
								>
									Add a note
								</Button>
							}
						/>
					)}
				</div>
			</PageBody>
		</>
	);
}
